import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { ContentLanguageHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";
import type { EntityDetailsQueryOptions } from "@/types/query-types";

export const useCommunityGroupQueries = () => {
  const projectId = useProjectId();
  const api = useApi();

  return {
    list: (queryArgs?: ApiQueryParams<"getGroupsV2">) =>
      queryOptions({
        queryKey: QUERY_KEYS.COMMUNITY_GROUPS_QUERY(projectId, queryArgs),
        queryFn: () => api.getGroupsV2(queryArgs),
        select: (data) => commonAPIDataSelector(data).items,
      }),

    details: ({ groupId }: { groupId: string }, options?: EntityDetailsQueryOptions) => {
      const headers = {
        [ContentLanguageHeaderName]: options?.translationMode || "default",
      };

      return queryOptions({
        queryKey: QUERY_KEYS.COMMUNITY_GROUP_DETAILS(projectId, groupId, options),
        queryFn: () => api.getGroupsDetailsV2(groupId, { headers }),
        select: commonAPIDataSelector,
      });
    },
    membersInfinite: (groupId: string, search?: string) => {
      const limit = 20;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.COMMUNITY_GROUP_MEMBERS(projectId, groupId, search || ""),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getGroupsMembersV1(groupId, {
              search,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 30 * 1000, // 30 seconds
      });
    },
  };
};
